import {
    PERSON_LOOKUP_FIELDS,
    PERSON_LOOKUP_FIELDS_BY_FIELD,
    PERSON_LOOKUP_POLICY_FIELDS,
    PERSON_LOOKUP_RESPONSE_TARGETS,
    normalizePersonLookupValue
} from './personLookupConfig';
import { PERSON_LOOKUP_POLICY_FIELDS as LEGACY_PERSON_LOOKUP_POLICY_FIELDS } from './legacy/personLookupConfig';

export default {
    data() {
        return {
            isFillingFromPersonLookup: false,
            personLookupAutofilledFields: {},
            personLookupAutofilledValues: {},
            personLookupAutofilledPreviousValues: {},
            personLookupFieldWatchers: [],
        };
    },

    mounted() {
        this.watchPersonLookupFields();
    },

    beforeDestroy() {
        this.unwatchPersonLookupFields();
    },

    methods: {
        watchPersonLookupFields() {
            this.unwatchPersonLookupFields();
            this.personLookupFieldWatchers = PERSON_LOOKUP_FIELDS.map(field =>
                this.$watch(`formData.${field.path}`, () => {
                    this.handlePersonLookupAutofilledFieldChange(field);
                })
            );
        },

        unwatchPersonLookupFields() {
            this.personLookupFieldWatchers.forEach(unwatch => unwatch());
            this.personLookupFieldWatchers = [];
        },

        handlePersonLookupAutofilledFieldChange(fieldDefinition) {
            if (this.isFillingFromPersonLookup) {
                return;
            }

            if (this.personLookupAutofilledFieldChanged(fieldDefinition)) {
                this.removePersonLookupAutofilledField(fieldDefinition.field);
            }
        },

        personLookupAutofilledFieldChanged({ field, path, type }) {
            if (!Object.prototype.hasOwnProperty.call(this.personLookupAutofilledValues, field)) {
                return false;
            }
            return this.personLookupAutofilledValues[field] !==
                normalizePersonLookupValue(this.valueFromPersonLookupPath(path), type);
        },

        removePersonLookupAutofilledField(field) {
            const fields = { ...this.personLookupAutofilledFields };
            const values = { ...this.personLookupAutofilledValues };
            const previousValues = { ...this.personLookupAutofilledPreviousValues };
            delete fields[field];
            delete values[field];
            delete previousValues[field];
            this.personLookupAutofilledFields = fields;
            this.personLookupAutofilledValues = values;
            this.personLookupAutofilledPreviousValues = previousValues;
        },

        removeEmptyPersonLookupAutofilledFields() {
            PERSON_LOOKUP_FIELDS.forEach(fieldDefinition => {
                if (!this.isPersonLookupAutofilled(fieldDefinition.field)) {
                    return;
                }

                const value = this.valueFromPersonLookupPath(fieldDefinition.path);
                if (!this.hasPersonLookupValue(value)) {
                    this.removePersonLookupAutofilledField(fieldDefinition.field);
                }
            });
        },

        isPersonLookupAutofilled(field) {
            return Boolean(this.personLookupAutofilledFields[field]);
        },

        setIfFilled(target, key, value) {
            const fieldDefinition = PERSON_LOOKUP_FIELDS_BY_FIELD[key];
            const isEmpty = value === null || value === undefined ||
                (fieldDefinition?.type !== 'boolean' && String(value).trim() === '');

            if (isEmpty) {
                return;
            }
            if (!target) {
                return;
            }
            const targetValue = fieldDefinition?.type === 'boolean'
                ? value === true || value === 1 || value === '1' || value === 'true'
                : value;

            this.storePersonLookupPreviousValue(target, key);

            target[key] = targetValue;
            this.personLookupAutofilledFields = { ...this.personLookupAutofilledFields, [key]: true };
            this.personLookupAutofilledValues = {
                ...this.personLookupAutofilledValues,
                [key]: normalizePersonLookupValue(targetValue, fieldDefinition ? fieldDefinition.type : null),
            };
        },

        setPersonLookupClearedValue(target, key) {
            if (!target) {
                return;
            }

            this.storePersonLookupPreviousValue(target, key);
            target[key] = null;
            this.personLookupAutofilledValues = {
                ...this.personLookupAutofilledValues,
                [key]: '',
            };
        },

        storePersonLookupPreviousValue(target, key) {
            if (Object.prototype.hasOwnProperty.call(this.personLookupAutofilledPreviousValues, key)) {
                return;
            }

            this.personLookupAutofilledPreviousValues = {
                ...this.personLookupAutofilledPreviousValues,
                [key]: target[key],
            };
        },

        applyPersonLookupData(lookupData) {
            PERSON_LOOKUP_RESPONSE_TARGETS.forEach(({ group, targetPath }) => {
                const target = targetPath.split('.').reduce((v, key) => (v ? v[key] : undefined), this);
                Object.entries(lookupData?.[group] ?? {}).forEach(([key, value]) => {
                    if (group === 'person' && key === 'deceased_date' && lookupData.person.deceased === false) {
                        this.setPersonLookupClearedValue(target, key);
                        return;
                    }

                    this.setIfFilled(target, key, value);
                });
            });
        },

        restorePersonLookupAutofilledFields() {
            Object.keys(this.personLookupAutofilledPreviousValues).forEach(field => {
                const fieldDefinition = PERSON_LOOKUP_FIELDS_BY_FIELD[field];

                if (!fieldDefinition) {
                    return;
                }

                const path = fieldDefinition.path.split('.');
                const key = path.pop();
                const target = path.reduce((value, pathPart) => value?.[pathPart], this.formData);

                if (target && key) {
                    target[key] = this.personLookupAutofilledPreviousValues[field];
                }
            });

            this.personLookupAutofilledFields = {};
            this.personLookupAutofilledValues = {};
            this.personLookupAutofilledPreviousValues = {};
        },

        storePersonLookupPolicyValues() {
            const policyFields = this.personLookupVecozo ? PERSON_LOOKUP_POLICY_FIELDS : LEGACY_PERSON_LOOKUP_POLICY_FIELDS;

            this.personLookupAutofilledValues = Object.values(policyFields)
                .reduce((values, { field, path, type }) => ({
                    ...values,
                    [field]: normalizePersonLookupValue(this.valueFromPersonLookupPath(path), type),
                }), this.personLookupAutofilledValues);
        },

        hasPersonLookupValue(value) {
            return normalizePersonLookupValue(value) !== '';
        },

        valueFromPersonLookupPath(path) {
            return path.split('.').reduce((v, key) => (v ? v[key] : undefined), this.formData);
        },
    },
};
